import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { CmmdBoardTask, CmmdProjectMilestone } from "@cmmd-forge/contracts";
import { useRef, type ReactNode } from "react";
import { withoutDragRole } from "~/components/data-views/boardDnd";
import { cn } from "~/lib/utils";
import { ProjectTaskMilestoneLaneHeader } from "./ProjectTaskMilestoneLaneHeader";
import type { TaskMilestoneGroup } from "./taskMilestoneGroups";
import { taskSwimlaneCellDropId } from "./taskMilestoneSwimlaneDnd";
import type { ProjectTaskBoardViewProps } from "./taskViewTypes";
import type { MilestoneStatusColumn } from "./taskMilestoneWorkflowColumns";

interface SwimlaneCardProps {
  readonly id: string;
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly mutationDisabled: boolean;
}

function SwimlaneCard({ id, children, onClick, mutationDisabled }: SwimlaneCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: mutationDisabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...withoutDragRole(attributes)}
      {...listeners}
      onClick={onClick}
      className={cn(
        "rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isDragging ? "opacity-50" : null,
      )}
    >
      {children}
    </div>
  );
}

interface SwimlaneColumnProps {
  readonly laneId: string;
  readonly status: string;
  readonly tasks: readonly CmmdBoardTask[];
  readonly renderCard: (task: CmmdBoardTask) => ReactNode;
  readonly onOpenTask: (taskId: number) => void;
  readonly taskListLabel: string;
  readonly mutationDisabled: boolean;
  /**
   * Drop-strip form used by a collapsed lane during a drag: same droppable, same drop id,
   * but short and card-less so the lane keeps its collapsed shape (see CMMD-2209).
   */
  readonly compact: boolean;
}

function SwimlaneColumn(props: SwimlaneColumnProps) {
  const { laneId, status, tasks, renderCard, onOpenTask } = props;
  const { setNodeRef, isOver } = useDroppable({
    id: taskSwimlaneCellDropId(laneId, status),
    disabled: props.mutationDisabled,
  });
  return (
    <div
      ref={setNodeRef}
      data-task-swimlane-column={status}
      className={cn(
        "flex w-[260px] shrink-0 flex-col overflow-hidden rounded-xl transition-colors duration-150",
        // 44 CSS px, not h-11: this app's root font-size is 14px, so rem-based Tailwind
        // spacing lands at 0.875x nominal (h-11 measures 38.5px) -- under the touch floor
        // for a target the user must hit with a finger mid-drag.
        //
        // The dashed edge marks a collapsed lane's drop strip as provisional -- it is a
        // target that only exists mid-drag, so it reads as an outline rather than as a
        // column that was there all along. Resting columns carry their own fill from the
        // board ladder (`--task-board-column`) and need no border to be found.
        props.compact
          ? "h-[44px] border border-dashed border-border"
          : "max-h-[min(70vh,43rem)] min-h-28",
        isOver ? "bg-[var(--task-board-drop)] ring-1 ring-ring" : "bg-[var(--task-board-column)]",
      )}
    >
      {props.compact ? (
        <div
          data-task-swimlane-task-list
          role="region"
          aria-label={props.taskListLabel}
          className="min-h-0 flex-1"
        />
      ) : (
        <SortableContext
          items={tasks.map((task) => String(task.id))}
          strategy={verticalListSortingStrategy}
        >
          <div
            data-task-swimlane-task-list
            role="region"
            aria-label={props.taskListLabel}
            className="flex min-h-20 flex-1 flex-col gap-2 overflow-y-auto p-2"
          >
            {tasks.length === 0 ? (
              <div className="min-h-20 flex-1" aria-hidden="true" />
            ) : (
              tasks.map((task) => (
                <SwimlaneCard
                  key={task.id}
                  id={String(task.id)}
                  onClick={() => onOpenTask(task.id)}
                  mutationDisabled={props.mutationDisabled}
                >
                  {renderCard(task)}
                </SwimlaneCard>
              ))
            )}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

interface MilestoneLaneColumnsProps {
  readonly group: TaskMilestoneGroup;
  readonly laneTasks: readonly CmmdBoardTask[];
  readonly statusColumns: readonly MilestoneStatusColumn[];
  readonly renderCard: (task: CmmdBoardTask) => ReactNode;
  readonly onOpenTask: (taskId: number) => void;
  readonly mutationDisabled: boolean;
  readonly compact: boolean;
}

function MilestoneLaneColumns(props: MilestoneLaneColumnsProps) {
  return (
    <div className="flex items-start gap-[12px]">
      {props.statusColumns.map((column) => (
        <SwimlaneColumn
          key={column.status}
          laneId={props.group.key}
          status={column.status}
          tasks={props.laneTasks.filter((task) =>
            column.tasks.some((columnTask) => columnTask.id === task.id),
          )}
          renderCard={props.renderCard}
          onOpenTask={props.onOpenTask}
          taskListLabel={`${props.group.label}, ${column.label} tasks`}
          mutationDisabled={props.mutationDisabled}
          compact={props.compact}
        />
      ))}
    </div>
  );
}

export function shouldExpandMilestoneLane(
  laneTasks: readonly CmmdBoardTask[],
  searchActive: boolean,
): boolean {
  if (searchActive && laneTasks.length > 0) return true;
  return laneTasks.some((task) => task.status !== "done");
}

interface MilestoneLaneSectionProps {
  readonly group: TaskMilestoneGroup;
  readonly laneTasks: readonly CmmdBoardTask[];
  readonly milestone: CmmdProjectMilestone | null;
  readonly statusColumns: readonly MilestoneStatusColumn[];
  readonly expansionOverride: boolean | undefined;
  readonly searchActive: boolean;
  readonly renderCard: (task: CmmdBoardTask) => ReactNode;
  readonly onOpenTask: (taskId: number) => void;
  readonly onToggle: (laneId: string, defaultExpanded: boolean) => void;
  readonly onManageMilestone: ProjectTaskBoardViewProps["onManageMilestone"];
  readonly mutationDisabled: boolean;
  /** True while a board drag is in flight; see the drop-strip note below (CMMD-2209). */
  readonly dragActive: boolean;
}

function milestoneManageHandler(
  milestoneId: number | null,
  onManageMilestone: ProjectTaskBoardViewProps["onManageMilestone"],
): (() => void) | undefined {
  if (milestoneId == null || onManageMilestone == null) return undefined;
  return () => onManageMilestone(milestoneId);
}

export function MilestoneLaneSection(props: MilestoneLaneSectionProps) {
  const autoExpanded = shouldExpandMilestoneLane(props.laneTasks, props.searchActive);
  // Auto-collapse is a first-impression default, not live state. Deriving it on every
  // render meant completing a lane's last open task flipped it to false mid-session, so
  // the lane -- every droppable cell and the card the user had just dropped into Done --
  // unmounted under them and the drag read as having failed. Latch it: once a lane has
  // been open, only the user's own toggle (expansionOverride) closes it. A lane that is
  // already fully done on arrival never latches, so it still opens collapsed.
  const hasBeenExpanded = useRef(autoExpanded);
  if (autoExpanded) hasBeenExpanded.current = true;
  const defaultExpanded = autoExpanded || hasBeenExpanded.current;
  const isExpanded =
    props.searchActive && props.laneTasks.length > 0
      ? true
      : (props.expansionOverride ?? defaultExpanded);
  const doneCount = props.laneTasks.filter((task) => task.status === "done").length;
  return (
    <section data-task-milestone-swimlane={props.group.key} className="space-y-[8px]">
      <div
        data-task-milestone-sticky-header={props.group.key}
        className="sticky top-[var(--task-board-status-rail-offset)] z-20 bg-[var(--task-board-canvas)]"
      >
        <ProjectTaskMilestoneLaneHeader
          label={props.group.label}
          status={props.milestone?.status ?? null}
          taskCount={props.laneTasks.length}
          doneCount={doneCount}
          endDate={props.milestone?.endDate ?? null}
          isExpanded={isExpanded}
          onToggle={() => props.onToggle(props.group.key, defaultExpanded)}
          onManage={milestoneManageHandler(props.group.milestoneId, props.onManageMilestone)}
        />
      </div>
      {/*
        A collapsed lane used to render nothing, and SwimlaneColumn is the only caller of
        useDroppable -- so a lane that arrived fully done had zero drop targets and could
        not receive a card at all, not even into its Done column. A drag holds the pointer,
        so the user could not expand it mid-drag to reach it either. While a drag is in
        flight a collapsed lane therefore renders a short, card-less strip carrying the
        same per-column droppables, so the card still lands in the column aimed at. It is
        gated on the drag so a finished lane keeps its deliberate collapsed shape at rest.
      */}
      {isExpanded || props.dragActive ? (
        <MilestoneLaneColumns
          group={props.group}
          laneTasks={props.laneTasks}
          statusColumns={props.statusColumns}
          renderCard={props.renderCard}
          onOpenTask={props.onOpenTask}
          mutationDisabled={props.mutationDisabled}
          compact={!isExpanded}
        />
      ) : null}
    </section>
  );
}
